import { useState } from "react";
import { FlipHorizontal2, RotateCcw, Ban } from "lucide-react";
import { BEAUTY_SLIDERS, RESHAPE_SLIDERS, BODY_SLIDERS, BEAUTY_QUICK_PRESETS, BEAUTY_DEFAULTS, FILTER_PRESETS, AR_STICKERS, MAKEUP_LOOKS } from "@/lib/beautyEngine";

const GOLD = "#E6B84C";

function SliderRow({ s, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <span className="text-[10px] font-bold text-zinc-300 w-[86px] shrink-0">{s.emoji} {s.label}</span>
      <input
        data-testid={testId}
        type="range" min={0} max={100} step={5} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer beauty-range"
        style={{ background: `linear-gradient(to right, ${GOLD} ${value}%, rgba(255,255,255,0.15) ${value}%)` }}
      />
      <span className={`text-[10px] font-bold w-7 text-right ${value === 0 ? "text-zinc-500" : "text-[#FDE08B]"}`}>{value === 0 ? "Off" : value}</span>
    </div>
  );
}

const TABS = [
  ["beauty", "✨ Beauty"],
  ["makeup", "💄 Makeup"],
  ["reshape", "💎 Reshape"],
  ["body", "🧍 Body"],
  ["filters", "🎨 Filters"],
  ["ar", "😎 AR"],
];

export default function BeautyTray({ params, onParams, presetKey, onPreset, sticker = "none", onSticker, makeup = "none", onMakeup, mirrored, onMirror, initialTab = "beauty", testPrefix = "" }) {
  const [tab, setTab] = useState(initialTab);
  const t = (s) => `${testPrefix}${s}`;
  const set = (key) => (v) => onParams({ ...params, [key]: v });
  const presetKeys = ["smooth", "brighten", "whiten", "rosy", "glow"];

  return (
    <div className="glass-dark rounded-2xl p-3 border border-[#E6B84C]/20" data-testid={t("beauty-tray")}>
      <div className="flex items-center gap-1 mb-2.5 overflow-x-auto no-scrollbar">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={t(`tray-tab-${k}`)} onClick={() => setTab(k)}
            className={`shrink-0 px-2.5 py-1.5 rounded-full text-[10px] font-bold transition-colors ${tab === k ? "text-[#0B0F19]" : "text-zinc-400 bg-white/5"}`}
            style={tab === k ? { background: `linear-gradient(90deg, ${GOLD}, #FDE08B)` } : undefined}>
            {label}
          </button>
        ))}
        <span className="flex-1" />
        {onMirror && (
          <button data-testid={t("mirror-toggle")} onClick={onMirror}
            className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold ${mirrored ? "text-[#E6B84C] bg-[#E6B84C]/10" : "text-zinc-400 bg-white/5"}`}>
            <FlipHorizontal2 size={11} /> Mirror
          </button>
        )}
      </div>

      {tab === "beauty" && (
        <div>
          <div className="flex gap-1.5 mb-2.5">
            {BEAUTY_QUICK_PRESETS.map((q) => (
              <button key={q.key} data-testid={t(`beauty-preset-${q.key}`)} onClick={() => onParams({ ...params, ...q.params })}
                className={`flex-1 py-1.5 rounded-full text-[10px] font-bold border transition-colors ${presetKeys.every((k) => (params[k] || 0) === q.params[k]) ? "border-[#E6B84C] text-[#FDE08B] bg-[#E6B84C]/10" : "border-white/10 text-zinc-400"}`}>
                {q.name}
              </button>
            ))}
            <button data-testid={t("beauty-reset")} onClick={() => onParams({ ...BEAUTY_DEFAULTS })}
              className="px-2.5 rounded-full text-zinc-400 border border-white/10"><RotateCcw size={11} /></button>
          </div>
          {BEAUTY_SLIDERS.map((s) => (
            <SliderRow key={s.key} s={s} value={params[s.key] || 0} onChange={set(s.key)}
              testId={s.key === "smooth" ? t("beauty-slider") : t(`beauty-slider-${s.key}`)} />
          ))}
        </div>
      )}

      {tab === "makeup" && (
        <div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
            {MAKEUP_LOOKS.map((m) => (
              <button key={m.key} data-testid={t(`makeup-${m.key}`)} onClick={() => onMakeup && onMakeup(m.key)}
                className={`shrink-0 flex flex-col items-center gap-1 ${makeup === m.key ? "" : "opacity-70"}`}>
                <span className={`w-12 h-12 rounded-full border-2 flex items-center justify-center text-base ${makeup === m.key ? "border-[#E6B84C] shadow-[0_0_10px_rgba(230,184,76,0.7)]" : "border-white/20"}`}
                  style={{ background: m.swatch }}>{m.key === "none" ? "" : "💄"}</span>
                <span className={`text-[9px] font-bold whitespace-nowrap ${makeup === m.key ? "text-[#FDE08B]" : "text-zinc-300"}`}>{m.name}</span>
              </button>
            ))}
          </div>
          <p className="text-[9px] text-zinc-500 mt-1">One-tap looks — lipstick, blush & eyeliner track your face live</p>
        </div>
      )}

      {tab === "reshape" && (
        <div>
          {RESHAPE_SLIDERS.map((s) => (
            <SliderRow key={s.key} s={s} value={params[s.key] || 0} onChange={set(s.key)}
              testId={t(`beauty-slider-${s.key}`)} />
          ))}
          <p className="text-[9px] text-zinc-500 mt-1">Reshape tracks your face live — subtle settings look most classy</p>
        </div>
      )}

      {tab === "body" && (
        <div>
          {BODY_SLIDERS.map((s) => (
            <SliderRow key={s.key} s={s} value={params[s.key] || 0} onChange={set(s.key)}
              testId={t(`beauty-slider-${s.key}`)} />
          ))}
          <p className="text-[9px] text-zinc-500 mt-1">Body restyle follows you live — works best when your upper body is in frame</p>
        </div>
      )}

      {tab === "filters" && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar py-1">
          {FILTER_PRESETS.map((f) => (
            <button key={f.key} data-testid={t(`filter-${f.key}`)} onClick={() => onPreset(f.key)}
              className={`shrink-0 flex flex-col items-center gap-1 ${presetKey === f.key ? "" : "opacity-70"}`}>
              <span className={`w-12 h-12 rounded-full border-2 overflow-hidden ${presetKey === f.key ? "border-[#E6B84C] shadow-[0_0_10px_rgba(230,184,76,0.7)]" : "border-white/20"}`}
                style={{ background: f.swatch }} />
              <span className={`text-[9px] font-bold whitespace-nowrap ${presetKey === f.key ? "text-[#FDE08B]" : "text-zinc-300"}`}>{f.name}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "ar" && (
        <div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar py-2" data-testid={t("sticker-row")}>
            {AR_STICKERS.map((s) => (
              <button key={s.key} data-testid={t(`sticker-${s.key}`)} onClick={() => onSticker && onSticker(s.key)}
                className={`shrink-0 flex flex-col items-center gap-1 ${sticker === s.key ? "" : "opacity-70"}`}>
                <span className={`w-12 h-12 rounded-full border-2 bg-white/5 flex items-center justify-center overflow-hidden ${sticker === s.key ? "border-[#E6B84C] shadow-[0_0_10px_rgba(230,184,76,0.7)]" : "border-white/20"}`}>
                  {s.icon ? <img src={s.icon} alt={s.name} className="w-9 h-9 object-contain" /> : <Ban size={16} className="text-zinc-500" />}
                </span>
                <span className={`text-[9px] font-bold whitespace-nowrap ${sticker === s.key ? "text-[#FDE08B]" : "text-zinc-300"}`}>{s.name}</span>
              </button>
            ))}
          </div>
          <p className="text-[9px] text-zinc-500 mt-1">Face effects track your face live — works best in good light</p>
        </div>
      )}
    </div>
  );
}
